import React, { useCallback, useMemo } from 'react';
import { Typography, Chip, Divider, Tooltip, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CancelIcon from '@mui/icons-material/Cancel';
import { TimesheetLabel } from '../labelSchema';
import { useLabelName } from '../../common/hooks/useLabelName';
const useStyles = makeStyles((theme) => ({
  container: {
    flexDirection: 'column',
    display: 'flex',
    padding: theme.spacing(0, 2)
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth: theme.spacing(28),
    marginRight: theme.spacing(0.25)
  },
  name: {
    maxWidth: '50%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    alignSelf: 'center',
    ...theme.typography.caption
  },
  value: {}
}));

type StyleProps = {
  notSelected?: boolean;
};

const useChipStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '50%',
    backgroundColor: theme.palette.background.paper,
    '& .MuiChip-deleteIcon': {
      margin: theme.spacing(0, -1, 0, 0)
    }
  },
  label: {
    padding: theme.spacing(0, 0.25),
    ...theme.typography.caption,
    fontStyle: ({ notSelected }: StyleProps) =>
      notSelected ? 'italic' : undefined,
    color: ({ notSelected }: StyleProps) =>
      notSelected ? '#666666' : undefined,
    justifyContent: 'flex-start'
  }
}));

type BaseProps = {
  disabled?: boolean;
  onDelete?: (value: TimesheetLabel) => void;
};

type DependantLabelsProps = BaseProps & {
  dependantLabels: TimesheetLabel[];
};

type DependantLabelItemProps = BaseProps & {
  label: TimesheetLabel;
};

export const DependantLabels: React.FC<DependantLabelsProps> = ({
  dependantLabels,
  disabled = false,
  onDelete
}: DependantLabelsProps) => {
  const classes = useStyles();
  const labels = useMemo(
    () => dependantLabels.filter((label) => label?.value?.stringValue),
    [dependantLabels]
  );

  return (
    <div className={classes.container}>
      {labels.map((label) => (
        <div key={label.id}>
          <DependantLabelItem
            label={label}
            disabled={disabled}
            onDelete={onDelete}
          />
          <Divider key={`${label.id}-divider`} />
        </div>
      ))}
    </div>
  );
};

export const DependantLabelItem: React.FC<DependantLabelItemProps> = ({
  label,
  disabled = false,
  onDelete
}: DependantLabelItemProps) => {
  const classes = useStyles();
  const chipClasses = useChipStyles({
    notSelected: label?.value?.idValue === undefined
  });
  const labelName = useLabelName(label);
  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(label);
  }, [onDelete, label]);

  const deleteIcon = useMemo(
    () => (
      <IconButton data-testid={`delete-${label.type}`} size="small">
        <CancelIcon />
      </IconButton>
    ),
    [label]
  );

  return (
    <div
      className={classes.item}
      key={label?.id}
      data-testid={`dependant-label-${label.type}`}
    >
      <Typography className={classes.name}>{labelName}</Typography>
      <Tooltip title={label?.value?.stringValue || ''}>
        <Chip
          classes={chipClasses}
          label={label?.value?.stringValue}
          onDelete={!disabled ? handleDelete : undefined}
          data-testid={`dependant-label-value-${label.type}`}
          deleteIcon={deleteIcon}
        />
      </Tooltip>
    </div>
  );
};

export default DependantLabels;
